import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTask, listTasks, updateGoal, updateTask, type Goal } from '../api';
import { NO_NEXT_STEP_LABEL } from '../goalVocab';
import { useAccountDay } from '../accountDay';

/**
 * What the app has noticed about a goal, and what to press about it.
 *
 * These three callouts were written inside the Goals *tree* and only ever rendered there — so the
 * screen you open to work on one goal, which is where somebody has already decided to act, showed
 * a bare "Pace: behind" and offered nothing at all. A DOM sweep of goal detail found zero buttons
 * in its main column beyond the task titles and "← All goals". The diagnosis lived on one screen
 * and the remedy on the other.
 *
 * Lifted here whole rather than reimplemented: the wording of these three is the best writing in
 * the product and two copies of it would drift apart on the first edit.
 */
export function GoalCallouts({
  goal,
  pace,
  momentum,
}: {
  goal: Pick<Goal, 'id' | 'title'>;
  pace: { nextActionState: string };
  momentum: { state: string; daysSinceMoved?: number | null };
}) {
  const queryClient = useQueryClient();
  const [addingAction, setAddingAction] = useState(false);
  /* The account's day, not the browser's — see `useAccountDay`. This planned work for the laptop's
     date, which is the account's only while the two happen to be in the same zone. */
  const { today } = useAccountDay();

  /*
    The goal's own open tasks, fetched only when a callout would use them — a dormant goal is a
    minority of rows and this is a request per goal.
  */
  const dormantTasks = useQuery({
    queryKey: ['tasks', { goalId: goal.id, forDormant: true }],
    queryFn: () => listTasks({ goalId: goal.id }),
    enabled: momentum.state === 'dormant',
  });

  /*
    The smallest open task, because the point of the prompt is to make restarting cheap — the goal
    has not moved in weeks, and the thing most likely to move it is the one that costs least.
    Smallest by estimate, then by points; a task with neither sorts last, since an unestimated task
    is not evidence of being small.
  */
  const smallestOpen = useMemo(
    () =>
      [...(dormantTasks.data ?? [])]
        .filter((t) => t.status !== 'done' && t.status !== 'dropped')
        .sort(
          (a, b) =>
            (a.estimateMinutes ?? Number.MAX_SAFE_INTEGER) -
              (b.estimateMinutes ?? Number.MAX_SAFE_INTEGER) ||
            (a.complexityPoints ?? Number.MAX_SAFE_INTEGER) -
              (b.complexityPoints ?? Number.MAX_SAFE_INTEGER),
        )[0],
    [dormantTasks.data],
  );

  const planSmallest = useMutation({
    // The account's today, as everywhere else that plans something.
    mutationFn: (id: string) => updateTask(id, { scheduledDate: today }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  /*
    Finishing a goal, from the place that noticed it was finished.

    It was always possible — open the edit form, find the status dropdown, choose "done", save —
    which is four interactions and a hunt, for the one moment in a goal's life worth making easy.
  */
  const markDone = useMutation({
    mutationFn: () => updateGoal(goal.id, { status: 'done' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <>
      {/*
    Nothing open under this goal — and *why*, which changes everything about what to say.

    A goal that still reads 60% done with nothing open is the most common way a long goal
    quietly dies. But a goal at 100% with every task complete is the opposite event, and it
    used to get the identical treatment: an amber warning reading "no next action", and one
    offered remedy, "add the next step". Somebody who has just finished a goal was being
    warned about it and asked to do more work. Marking it done was not offered here at all —
    it meant opening the edit form and changing a dropdown.
  */}
      {pace.nextActionState === 'ready_to_finish' && (
        <div className="goal-finished" data-testid="goal-ready-to-finish">
          <span className="pill ok">ready to finish</span>{' '}
          <span className="subtext">
            Every task under this goal is done. Only you can say the goal is.
          </span>{' '}
          <button
            type="button"
            disabled={markDone.isPending}
            onClick={() => markDone.mutate()}
            data-testid="mark-goal-done"
          >
            Mark it done
          </button>{' '}
          {/* Still offered, quietly: "finished the list" and "finished the goal" are not the
          same claim, which is exactly why the app must not make it for you. */}
          <button className="link" onClick={() => setAddingAction((v) => !v)}>
            {addingAction ? 'cancel' : 'or add more'}
          </button>
          {addingAction && (
            <NextActionForm goalId={goal.id} onDone={() => setAddingAction(false)} />
          )}
        </div>
      )}

      {/*
    Dormant, with something to press.

    `ready to finish` and `no next step` each pair their diagnosis with a working button,
    which is exactly why this one stood out: the app identifies the goal most in need of a
    decision and offered no way to make one. The badge said "dormant · 55d" and left you to go
    and find the goal's tasks yourself.

    The smallest open task, because the point of the prompt is to make restarting cheap — the
    goal has not moved in weeks, and the thing most likely to move it is the one that costs
    least. Smallest by estimate, then by points; a task with neither sorts last, since an
    unestimated task is not evidence of being small.
  */}
      {momentum.state === 'dormant' && smallestOpen && (
        <div className="goal-dormant" data-testid="goal-dormant">
          {/*
            No pill here: the row this sits under already carries `dormant · 51d`, and printing it
            again put the same three words twice on one line. What this block adds is the thing to
            do about it, so that is what it leads with.
          */}
          <span className="subtext">Dormant. Pick one small thing to start it again.</span>{' '}
          <button
            type="button"
            disabled={planSmallest.isPending}
            onClick={() => planSmallest.mutate(smallestOpen.id)}
            data-testid="plan-smallest"
            title={smallestOpen.title}
          >
            Plan “{smallestOpen.title}” for today
          </button>
        </div>
      )}

      {pace.nextActionState === 'not_started' && (
        <div className="goal-stalled" data-testid="goal-needs-action">
          <span className="pill warn">{NO_NEXT_STEP_LABEL}</span>{' '}
          <span className="subtext">
            Nothing is attached to this goal yet — no tasks, and no habit serving it.
          </span>{' '}
          <button className="link" onClick={() => setAddingAction((v) => !v)}>
            {addingAction ? 'cancel' : 'add the next step'}
          </button>
          {addingAction && (
            <NextActionForm goalId={goal.id} onDone={() => setAddingAction(false)} />
          )}
        </div>
      )}
    </>
  );
}

/**
 * The remedy offered beside the diagnosis, rather than three screens away.
 *
 * Moved here verbatim with the callouts that use it. The first attempt at this move rewrote it
 * from memory and quietly changed its placeholder and its button label — which an existing spec
 * caught, and which is the argument for moving code rather than re-describing it.
 */
function NextActionForm({ goalId, onDone }: { goalId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');

  const add = useMutation({
    mutationFn: () => createTask({ title: title.trim(), status: 'next', goalIds: [goalId] }),
    onSuccess: () => {
      setTitle('');
      onDone();
      void queryClient.invalidateQueries();
    },
  });

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) add.mutate();
      }}
    >
      <input
        autoFocus
        value={title}
        placeholder="The very next physical step"
        onChange={(e) => setTitle(e.target.value)}
      />
      <button type="submit" disabled={!title.trim() || add.isPending}>
        Add task
      </button>
    </form>
  );
}
